// employees/EmployeeContext.js
import { exportEmployeeApi, getEmployeesApi } from "api/services/employee";
import { format } from "date-fns";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { filterHandler } from 'utils/common';
import { defaultPagination, handlePageChange } from "utils/table";
import useEmployeeFilter from "./../hooks/filterHook";


// Create the Employee context

export const EmployeeContext = createContext();

// Custom hook for accessing Employee context easily
export const useEmployee = () => {
  return useContext(EmployeeContext);
};

// EmployeeProvider component to wrap around components needing access to employee data
export const EmployeeProvider = ({ children }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

    const navigate = useNavigate()

    const location = useLocation()
  // Employee state (could be an array of employees, single employee, etc.)
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(0);

  
  const [employeeData, setEmployeeData] = useState([]);
  const [view, setView] = useState(isMobile? "grid":"list");
  const [showFilterDrawer, setShowFilterDrawer] = useState(false)
  
  const [appliedFilterCount, setAppliedFilterCount] = useState(0);

  // Calculate cards per row based on screen size
  const getCardsPerRow = () => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 1400) return 3; // xl: col-xl-4 (3 cards)
      if (width >= 992) return 2;  // lg: col-lg-6 (2 cards)
      if (width >= 768) return 2;  // md: col-md-6 (2 cards)
      return 1; // sm: col-sm-12 (1 card)
    }
    return 3; // default
  };

  // Adjust page size to be multiple of cards per row
  const adjustPageSizeForGrid = (currentPageSize, cardsPerRow) => {
    if (cardsPerRow <= 1) return currentPageSize; // No adjustment needed for single column
    const remainder = currentPageSize % cardsPerRow;
    if (remainder === 0) return currentPageSize;
    return currentPageSize + (cardsPerRow - remainder);
  };

  const initialPageSize = searchParams.get("size") ? parseInt(searchParams.get("size")) : 50;
  const cardsPerRow = getCardsPerRow();
  const adjustedPageSize = view === "grid" ? adjustPageSizeForGrid(initialPageSize, cardsPerRow) : initialPageSize;

  const [pageSettings , setPageSettings] = useState({
    ...defaultPagination,
    page: searchParams.get("page") || 1,
    pageSize: adjustedPageSize,
    sortType: searchParams.get("sort_type") || "",
    sortBy: searchParams.get("sort_byb") || "",

})
const {     handleFilterChange,
    filters,
    handleInputChange, filterLoading, setFilterLoading, handleDateChange, handleClearAll} = useEmployeeFilter(pageSettings , setPageSettings);

const getFilterParams = useCallback(()=>{
  let filter_params = filterHandler(filters)
    // let _filters = [...filters].filter(item=> item.value)
    // const filter_params = _filters.reduce((acc, item) => {
    //   if(Array.isArray(item.value)){
    //     acc[item.key] = item.value.map(i=> i.value).join(",")
    //   }else{
    //     acc[item.key] = item.value;

    //   }
    //     return acc;
    //     }, {});
    return filter_params
        
})

const setEmployeeParams = (pageData) => {
    let filter_params = getFilterParams()
    let params = {
        size: pageData?.pageSize,
        page: pageData?.page,
        sort_by: pageData?.sortBy || "",
        sort_type: pageData?.sortType || "",
        ...filter_params
    }
    setSearchParams(params)
}
// handleInputChange = (value, key)=>{
//     setPageSettings({
//         ...pageSettings,
//         page: 1,
//         filters
//       })
//       handleSearchInput(value, key))
// }


    const getEmployeeList = () => {
        const isLoadMore = view === "grid" && pageSettings?.page > 1 && employeeData.length > 0;
        
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        setFilterLoading(true)

        // let _filters = [...filters].filter(item=> item.value)
        // const filter_params = _filters.reduce((acc, item) => {
        //     acc[item.key] = item.value;
        //     return acc;
        //   }, {});
        let filter_params = getFilterParams()

        if(filter_params?.employment_date_after){
            filter_params.employment_date_after = format(new Date(filter_params.employment_date_after), "yyyy-MM-dd")
        }
        if(filter_params?.employment_date_before){
            filter_params.employment_date_before = format(new Date(filter_params.employment_date_before), "yyyy-MM-dd")
        }


          
        getEmployeesApi({
          sort_by:pageSettings?.sortBy,
          sort_type: pageSettings?.sortType,
          page_size: pageSettings?.pageSize,
          page: pageSettings?.page,
          ...filter_params
    
        }).then(response=>{
          console.log(response.data.results)
          
          if(view=="grid" && pageSettings?.page!==1){
            const previousLength = employeeData.length;
            // Create a map of existing employees by ID to preserve object references
            const existingEmployeesMap = new Map(employeeData.map(emp => [emp.id, emp]));
            // Only use new object references for employees that don't exist yet
            const newEmployees = response.data.results.filter(emp => !existingEmployeesMap.has(emp.id));
            // Preserve existing object references, only add new ones
            setEmployeeData([...employeeData, ...newEmployees]);
            
            // Scroll to the first new item after a short delay to allow DOM update
            setTimeout(() => {
              const newItemIndex = previousLength;
              const element = document.querySelector(`[data-employee-index="${newItemIndex}"]`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Add a temporary highlight to show new data
                element.style.transition = 'background-color 0.3s ease';
                element.style.backgroundColor = 'rgba(102, 126, 234, 0.1)';
                setTimeout(() => {
                  element.style.backgroundColor = '';
                }, 2000);
              }
            }, 100);

          }else{
            setEmployeeData(response.data.results);

          }
          setPageSettings({
            ...pageSettings,
            total: response.data.count
          })
    
          setLoading(false)
          setLoadingMore(false)
          setFilterLoading(false)
          setShowFilterDrawer(false)
        }).catch(e=>{
          setLoading(false)
          setLoadingMore(false)
        })
    }



  // Use ref to track previous filters to prevent unnecessary API calls
  const prevFiltersRef = useRef(null);

  // Adjust page size when view changes to grid
  useEffect(() => {
    if (view === "grid") {
      const cardsPerRow = getCardsPerRow();
      const currentPageSize = pageSettings?.pageSize || 50;
      const adjustedSize = adjustPageSizeForGrid(currentPageSize, cardsPerRow);
      
      if (adjustedSize !== currentPageSize) {
        setPageSettings(prev => ({
          ...prev,
          pageSize: adjustedSize
        }));
      }
    }
  }, [view]);

  // Handle window resize for grid view
  useEffect(() => {
    if (view !== "grid") return;

    const handleResize = () => {
      const cardsPerRow = getCardsPerRow();
      const currentPageSize = pageSettings?.pageSize || 50;
      const adjustedSize = adjustPageSizeForGrid(currentPageSize, cardsPerRow);
      
      if (adjustedSize !== currentPageSize) {
        setPageSettings(prev => ({
          ...prev,
          pageSize: adjustedSize
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [view, pageSettings?.pageSize]);

  useEffect(()=>{
    // Only call API if filters actually changed (deep comparison)
    const filtersChanged = JSON.stringify(pageSettings?.filters) !== JSON.stringify(prevFiltersRef.current);
    
    if (filtersChanged || 
        pageSettings?.page !== prevFiltersRef.current?.page ||
        pageSettings?.pageSize !== prevFiltersRef.current?.pageSize ||
        pageSettings?.sortBy !== prevFiltersRef.current?.sortBy ||
        pageSettings?.sortType !== prevFiltersRef.current?.sortType) {
      getEmployeeList();
      prevFiltersRef.current = {
        filters: pageSettings?.filters,
        page: pageSettings?.page,
        pageSize: pageSettings?.pageSize,
        sortBy: pageSettings?.sortBy,
        sortType: pageSettings?.sortType
      };
    }
  },[pageSettings?.page, pageSettings?.pageSize, pageSettings?.sortBy, pageSettings?.sortType, pageSettings?.filters])

  useEffect(()=>{
    if(location.pathname === "/employees"){
      setEmployeeParams(pageSettings)
    }
  },[pageSettings])

  const handleApplyFilter = () => {
    let _appliedFilters = filters.filter(item=> item.value && item.key != "search");
    console.log(_appliedFilters, "---_appliedFilters")
    setAppliedFilterCount(_appliedFilters.length) 

    setPageSettings({
        ...pageSettings,
        page: 1,
        filters
      })
    // getEmployeeList()


  }

  const handleGridChange = (page, size) =>{
    console.log(page, size, ")))))))))))))")
    setPageSettings({
      ...pageSettings,
      pageSize: size,
      page,
    })
    // setSearchParams({
    //     size,
    //     page,
    // })
  }
  const handleChange = (pagination, filters, sorter) => {
    let pageData = handlePageChange(pagination, filters, sorter, pageSettings)
    console.log(pageData)
    // setSearchParams({
    //     size: pageData?.pageSize,
    //     page: pageData?.page,
    //     sort_by: pageData?.sortBy || "",
    //     sort_type: pageData?.sortType || ""
    // })
    setPageSettings(pageData)
  };

  const handleFilterDrawer = () => {
    console.log("JJJJJJJ")
    setShowFilterDrawer(!showFilterDrawer)
    setFilterLoading(false)
  }


  const handleEmployeeDetailPage = useCallback((id, index) => {
    navigate(`/employees/${id}`)
    setSelectedPage(index)
  }, [navigate])


  const handleExport = () => {
    let filter_params = getFilterParams()

    if(filter_params?.employment_date_after){
      filter_params.employment_date_after = format(new Date(filter_params.employment_date_after), "yyyy-MM-dd")
    }
    if(filter_params?.employment_date_before){
      filter_params.employment_date_before = format(new Date(filter_params.employment_date_before), "yyyy-MM-dd")
    }

    setExportLoading(true)
      
    exportEmployeeApi({
      sort_by: pageSettings?.sortBy,
      sort_type: pageSettings?.sortType,
      page_size: pageSettings?.pageSize,
      page: pageSettings?.page,
      ...filter_params
    }, { 
      responseType: 'blob'
    }).then(response => {
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      
      const filename = 'employee_export.xlsx';
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      setExportLoading(false);
    }).catch(e => {
      console.error('Export failed:', e);
      setExportLoading(false);
    });
  }
  // Value to be provided to context consumers
  // Memoize the context value to avoid unnecessary re-renders
  const value = useMemo(() => ({
    employeeData, setEmployeeData,
    loading, setLoading,
    loadingMore, setLoadingMore,
    view, setView,
    pageSettings , setPageSettings,
    showFilterDrawer, setShowFilterDrawer,

    filterLoading, setFilterLoading,
    selectedPage, setSelectedPage,

    handleGridChange,
    handleChange,
    handleFilterDrawer,

    handleFilterChange,
    filters,
    handleInputChange,
    handleApplyFilter,
    handleEmployeeDetailPage,
    handleDateChange,
    handleClearAll,
    appliedFilterCount,
    handleExport,
    exportLoading, setExportLoading
    
  }), [employeeData , view, loading, loadingMore, pageSettings, showFilterDrawer, filters, filterLoading, selectedPage, appliedFilterCount, exportLoading]);

  return (
    <EmployeeContext.Provider value={value}>
      {children}
    </EmployeeContext.Provider>
  );
};